import React, { forwardRef, useImperativeHandle } from "react";
import { User } from "@supabase/supabase-js";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AvatarDisplay from "./AvatarDisplay";
import AvatarCropSlider from "./components/AvatarCropSlider";
import AvatarFeedback from "./components/AvatarFeedback";
import AvatarUploadButton from "./components/AvatarUploadButton";
import { useAvatarUpload } from "./hooks/useAvatarUpload";
import { Badge } from "@/components/ui/badge";
import { Info } from "lucide-react";

interface EnhancedAvatarUploadProps {
  user: User;
  avatarPreview: string | null;
  onAvatarChange: (avatarUrl: string) => void;
  onProcessedImageChange?: (imageUrl: string, processedBlob?: Blob) => void;
  isUploading?: boolean;
}

export interface EnhancedAvatarUploadRef {
  uploadPendingAvatar: () => Promise<string | null>;
  hasPendingUpload: boolean;
}

const EnhancedAvatarUpload = forwardRef<EnhancedAvatarUploadRef, EnhancedAvatarUploadProps>(({
  user,
  avatarPreview,
  onAvatarChange,
  onProcessedImageChange,
  isUploading = false,
}, ref) => {
  const {
    fileInputRef,
    processingError,
    isProcessing,
    qualityFeedback,
    cropScale,
    selectedFile,
    rawPreview,
    triggerFileInput,
    setCropScale,
    handleFileChange,
    compressionStats,
    uploadPendingAvatar,
    hasPendingUpload,
  } = useAvatarUpload({
    user,
    avatarPreview,
    onAvatarChange,
    onProcessedImageChange,
    isUploading,
  });

  // Expose uploadPendingAvatar method to parent via ref
  useImperativeHandle(ref, () => ({
    uploadPendingAvatar,
    hasPendingUpload,
  }));

  const isStorageLimited = avatarPreview?.startsWith("data:");

  return (
    <div className="space-y-3">
      <AvatarDisplay
        displayImage={avatarPreview}
        isLoading={isUploading || isProcessing}
        error={processingError}
        triggerFileInput={triggerFileInput}
      />

      <Input
        id="avatar"
        type="file"
        ref={fileInputRef}
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileChange}
        className="hidden"
        disabled={isUploading || isProcessing}
      />

      <p className="text-xs text-muted-foreground">
        Supported formats: JPEG, PNG, GIF, WEBP
      </p>

      {selectedFile && (
        <AvatarCropSlider
          cropScale={cropScale}
          setCropScale={setCropScale}
          isProcessing={isProcessing}
        />
      )}

      {compressionStats && (
        <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <Info className="h-3 w-3" />
          <span>{compressionStats}</span>
        </div>
      )}

      <AvatarFeedback
        qualityFeedback={qualityFeedback}
        processingError={processingError}
        isStorageLimited={isStorageLimited}
      />
    </div>
  );
});

EnhancedAvatarUpload.displayName = "EnhancedAvatarUpload";

export default EnhancedAvatarUpload;
